import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collectionApi, resolveCollectionApiError } from '../services/collectionApi';
import { salesApi, resolveSalesApiError } from '../services/salesApi';
import { branchApi, resolveBranchApiError } from '../services/branchApi';
import SaleDetailModal from '../components/sales/SaleDetailModal';
import CollectionsTab from '../components/collections/CollectionsTab';
import ARTab from '../components/collections/ARTab';
import CancelledARTab from '../components/collections/CancelledARTab';
import BankRemittanceTab from '../components/collections/BankRemittanceTab';
import useScopePermissions from '../hooks/useScopePermissions';
import { useAuth } from '../context/AuthContext';
import './CollectionsPage.css';

const TAB_ITEMS = [
  { key: 'collections', label: 'Collections' },
  { key: 'ar', label: 'Accounts Receivable (AR)' },
  { key: 'cancelled-ar', label: 'Cancelled AR' },
  { key: 'bank-remittance', label: 'Bank Remittance' },
];

export default function CollectionsPage() {
  const { hasScope } = useScopePermissions();
  const { isAdmin, userBranchName } = useAuth();
  const canPostArPayment = hasScope('collections.post-payment');
  const canCancelAr = hasScope('collections.cancel-ar');
  const canCreateRemittance = hasScope('collections.remittance.create');
  const canUpdateRemittance = hasScope('collections.remittance.update');
  const canCancelRemittance = hasScope('collections.remittance.cancel');
  const canDeleteRemittance = hasScope('collections.remittance.delete');

  const [activeTab, setActiveTab] = useState('collections');
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [collections, setCollections] = useState([]);
  const [sales, setSales] = useState([]);
  const [bankRemittances, setBankRemittances] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [viewingSale, setViewingSale] = useState(null);

  useEffect(() => {
    if (!isAdmin && userBranchName) {
      setSelectedBranch(userBranchName);
    }
  }, [isAdmin, userBranchName]);

  const loadData = useCallback(async (options = {}) => {
    const { clearFeedback = true } = options;

    setIsLoading(true);
    setError('');
    if (clearFeedback) {
      setMessage('');
    }

    try {
      const [
        collectionsResponse,
        salesResponse,
        remittancesResponse,
        branchesResponse,
      ] = await Promise.all([
        collectionApi.listCollections({ bustCache: true }),
        salesApi.listSales({ bustCache: true }),
        collectionApi.listBankRemittances({ bustCache: true }),
        branchApi.listBranches({ bustCache: true }),
      ]);

      setCollections(collectionsResponse?.data?.collections || []);
      setSales(salesResponse?.data?.sales || []);
      setBankRemittances(remittancesResponse?.data?.bank_remittances || []);
      setBranches(branchesResponse?.data?.branches || []);
    } catch (err) {
      const messageText = resolveCollectionApiError(err)
        || resolveSalesApiError(err)
        || resolveBranchApiError(err)
        || 'Failed to load collections module data.';
      setError(messageText);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const branchOptions = useMemo(() => {
    const branchNames = new Set();

    branches.forEach((branch) => {
      if (branch?.name) {
        branchNames.add(branch.name);
      }
    });
    collections.forEach((row) => {
      if (row?.branch) {
        branchNames.add(row.branch);
      }
    });
    sales.forEach((row) => {
      if (row?.branch) {
        branchNames.add(row.branch);
      }
    });
    bankRemittances.forEach((row) => {
      if (row?.branch) {
        branchNames.add(row.branch);
      }
    });

    return Array.from(branchNames).sort((left, right) => left.localeCompare(right));
  }, [branches, collections, sales, bankRemittances]);

  async function runSubmission(action, successMessage) {
    let success = false;

    setIsSubmitting(true);
    setError('');
    setMessage('');

    try {
      await action();
      setMessage(successMessage);
      await loadData({ clearFeedback: false });
      success = true;
    } catch (err) {
      setError(resolveCollectionApiError(err));
    } finally {
      setIsSubmitting(false);
    }

    return success;
  }

  async function handlePostArPayment(payload) {
    if (!canPostArPayment) {
      setError('You do not have permission to post AR payments.');
      return false;
    }

    return runSubmission(() => collectionApi.postArPayment(payload), 'AR payment posted successfully.');
  }

  async function handleUploadProofOfPayment(file) {
    const response = await collectionApi.uploadProofOfPayment(file);
    return response?.data?.proof_of_payment || null;
  }

  async function handleCancelAr(payload) {
    if (!canCancelAr) {
      setError('You do not have permission to cancel AR records.');
      return false;
    }

    return runSubmission(() => collectionApi.cancelAr(payload), 'AR cancelled successfully.');
  }

  async function handleCreateRemittance(payload) {
    if (!canCreateRemittance) {
      setError('You do not have permission to add bank remittances.');
      return false;
    }

    return runSubmission(() => collectionApi.createBankRemittance(payload), 'Bank remittance created successfully.');
  }

  async function handleUpdateRemittance(bankRemittanceId, payload) {
    if (!canUpdateRemittance) {
      setError('You do not have permission to edit bank remittances.');
      return false;
    }

    return runSubmission(
      () => collectionApi.updateBankRemittance(bankRemittanceId, payload),
      'Bank remittance updated successfully.'
    );
  }

  async function handleCancelRemittance(bankRemittanceId, payload) {
    if (!canCancelRemittance) {
      setError('You do not have permission to cancel bank remittances.');
      return false;
    }

    return runSubmission(
      () => collectionApi.cancelBankRemittance(bankRemittanceId, payload),
      'Bank remittance cancelled successfully.'
    );
  }

  async function handleUploadDepositSlip(file) {
    const response = await collectionApi.uploadDepositSlip(file);
    return response?.data?.deposit_slip || null;
  }

  async function handleDeleteRemittance(bankRemittanceId) {
    if (!canDeleteRemittance) {
      setError('You do not have permission to delete bank remittances.');
      return false;
    }

    if (!window.confirm('Are you sure you want to delete this remittance record?')) {
      return false;
    }

    return runSubmission(
      () => collectionApi.deleteBankRemittance(bankRemittanceId),
      'Bank remittance deleted successfully.'
    );
  }

  function renderActiveTab() {
    if (activeTab === 'collections') {
      return (
        <CollectionsTab
          collections={collections}
          sales={sales}
          selectedBranch={selectedBranch}
          isLoading={isLoading}
          onViewSale={setViewingSale}
        />
      );
    }

    if (activeTab === 'ar') {
      return (
        <ARTab
          sales={sales}
          collections={collections}
          selectedBranch={selectedBranch}
          isLoading={isLoading}
          isSubmitting={isSubmitting}
          onPostPayment={handlePostArPayment}
          onCancelAr={handleCancelAr}
          onUploadProof={handleUploadProofOfPayment}
          onViewSale={setViewingSale}
          canPostPayment={canPostArPayment}
          canCancelAr={canCancelAr}
        />
      );
    }

    if (activeTab === 'cancelled-ar') {
      return (
        <CancelledARTab
          collections={collections}
          sales={sales}
          selectedBranch={selectedBranch}
          isLoading={isLoading}
          onViewSale={setViewingSale}
        />
      );
    }

    return (
      <BankRemittanceTab
        bankRemittances={bankRemittances}
        collections={collections}
        selectedBranch={selectedBranch}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
        onCreate={handleCreateRemittance}
        onUpdate={handleUpdateRemittance}
        onCancel={handleCancelRemittance}
        onDelete={handleDeleteRemittance}
        onUploadDepositSlip={handleUploadDepositSlip}
        canCreateRemittance={canCreateRemittance}
        canUpdateRemittance={canUpdateRemittance}
        canCancelRemittance={canCancelRemittance}
        canDeleteRemittance={canDeleteRemittance}
      />
    );
  }

  return (
    <div className="dashboard-container">
      <section className="dashboard-header">
        <div>
          <h1>Collections</h1>
          <p>Monitor AR, posted collections, cancelled AR, and bank remittances.</p>
        </div>
        <div className="collections-header-actions">
          <label htmlFor="collections-branch-filter">Branch</label>
          <select
            id="collections-branch-filter"
            value={selectedBranch}
            onChange={(event) => setSelectedBranch(event.target.value)}
            disabled={!isAdmin}
          >
            {isAdmin ? <option value="all">All Branches</option> : null}
            {branchOptions.map((branchName) => (
              <option key={branchName} value={branchName}>
                {branchName}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="dashboard-content">
        <div className="collections-tabs">
          {TAB_ITEMS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`collections-tab-btn ${activeTab === tab.key ? 'is-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error ? <div className="error-box">{error}</div> : null}
        {message ? <div className="success-box">{message}</div> : null}

        {renderActiveTab()}
      </section>

      <SaleDetailModal
        isOpen={Boolean(viewingSale)}
        onClose={() => setViewingSale(null)}
        sale={viewingSale}
        collections={collections}
      />
    </div>
  );
}
